// Salary / compensation panel for an employee (HR/owner only). Set & revise the
// effective-dated base salary; shows history. Assigns the employee to payroll
// first if they aren't yet (a snapshot is required before pay can be set).
import { formatDate } from '@/lib/orgFormat';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Wallet, Loader2, History, Plus } from 'lucide-react';
import {
  getCompensation, setCompensation, assignToPayroll, listLegalEntities,
  type Compensation, type LegalEntityLite,
} from '@/lib/compensationApi';
import { RestrictedPayNotice } from '@/features/payroll/components/RestrictedValue';
import { isPayForbidden, isWithheld } from '@/features/payroll/lib/compensationAccess';

const todayIso = new Date().toISOString().slice(0, 10);

const fmtMoney = (amount: string, currency: string) => {
  const n = Number(amount);
  if (!isFinite(n)) return `${amount} ${currency}`;
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${currency}`;
};
const fmtDate = (s?: string | null) => (s ? formatDate(s + 'T00:00:00') : '—');

export const CompensationCard: React.FC<{
  employeeId: string;
  fullName?: string;
  email?: string;
  nationalId?: string;
}> = ({ employeeId, fullName, email, nationalId }) => {
  const { t } = useTranslation('payroll');
  const [history, setHistory] = useState<Compensation[] | null>(null);
  const [onPayroll, setOnPayroll] = useState(true);
  // 403 from the compensation read: the viewer may not see THIS person's pay (it can be
  // entity-scoped, so the role alone doesn't decide it). Rendered as restricted — never as
  // a load error, and never as "—" or 0, which both claim something about the pay.
  const [restricted, setRestricted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const [amount, setAmount] = useState('');
  const [effFrom, setEffFrom] = useState(todayIso);
  // Salary stays read-only until the user explicitly clicks "Revise salary".
  const [revising, setRevising] = useState(false);

  const [entities, setEntities] = useState<LegalEntityLite[]>([]);
  const [entityId, setEntityId] = useState('');

  const load = async () => {
    setLoading(true);
    setRestricted(false);
    try {
      const h = await getCompensation(employeeId);
      setHistory(h);
      setOnPayroll(true);
    } catch (e: any) {
      if (isPayForbidden(e)) {
        setRestricted(true);
        setHistory(null);
      } else if (e?.response?.status === 404) {
        setOnPayroll(false);
        setHistory(null);
        const ents = await listLegalEntities().catch(() => []);
        setEntities(ents);
        if (ents[0]) setEntityId(ents[0].id);
      } else {
        setNote({ kind: 'err', text: t('compensation.loadError') });
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, [employeeId]);

  const onAssign = async () => {
    if (!entityId) return;
    setBusy(true); setNote(null);
    try {
      await assignToPayroll(employeeId, { legal_entity_id: entityId, full_name: fullName, email, national_id: nationalId });
      await load();
    } catch (e: any) {
      setNote({ kind: 'err', text: e?.response?.data?.message || t('compensation.assignError') });
    } finally { setBusy(false); }
  };

  const onSave = async () => {
    if (!amount.trim()) return;
    setBusy(true); setNote(null);
    try {
      await setCompensation(employeeId, { amount: amount.trim(), effective_from: effFrom });
      setAmount('');
      setRevising(false);
      setNote({ kind: 'ok', text: t('compensation.salarySaved') });
      await load();
    } catch (e: any) {
      setNote({ kind: 'err', text: e?.response?.data?.message || t('compensation.saveError') });
    } finally { setBusy(false); }
  };

  const current = history?.find((c) => c.active) || history?.[0];
  const input = 'h-10 px-3 rounded-lg border border-border bg-card text-foreground text-sm focus:border-orange-400 focus:outline-none';
  const primaryBtn = 'h-10 px-4 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50';
  const secondaryBtn = 'h-10 px-4 rounded-lg border border-border text-foreground font-medium inline-flex items-center justify-center gap-2 hover:bg-muted disabled:opacity-50';

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader><CardTitle className="text-base text-orange-700 flex items-center gap-2"><Wallet className="w-4 h-4" /> {t('compensation.title')}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {note && (
          <div className={`rounded-lg px-3 py-2 text-sm border ${note.kind === 'ok' ? 'bg-success-soft border-transparent text-success-strong' : 'bg-danger-soft border-transparent text-danger-strong'}`}>{note.text}</div>
        )}

        {loading ? (
          <div className="py-6 flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('compensation.loading')}</div>
        ) : restricted ? (
          <RestrictedPayNotice />
        ) : !onPayroll ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('compensation.notOnPayroll')}</p>
            <div className="flex flex-wrap items-end gap-2">
              <select className={input} value={entityId} onChange={(e) => setEntityId(e.target.value)}>
                {entities.length === 0 && <option value="">{t('compensation.noLegalEntity')}</option>}
                {entities.map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}
              </select>
              <button className={primaryBtn} onClick={onAssign} disabled={busy || !entityId}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {t('compensation.assignToPayroll')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div>
              <div className="text-xs text-muted-foreground">{t('compensation.currentBaseSalary')}</div>
              <div className="text-2xl font-bold text-foreground">
                {current && isWithheld(current.amount)
                  ? <RestrictedPayNotice className="text-sm font-normal" />
                  : current ? fmtMoney(current.amount, current.currency) : '—'}
              </div>
              {current && <div className="text-xs text-muted-foreground">{t('compensation.since', { date: fmtDate(current.effective_from) })}</div>}
            </div>

            <div className="border-t border-border pt-3">
              {revising ? (
                <>
                  <div className="text-sm font-medium text-foreground mb-2 flex items-center gap-1.5"><Plus className="w-4 h-4" /> {t('compensation.reviseSalary')}</div>
                  <div className="flex flex-wrap items-end gap-2">
                    <label className="flex-1 min-w-[140px]">
                      <span className="block text-xs text-muted-foreground mb-1">{t('compensation.newMonthlyBase')}</span>
                      <input className={`${input} w-full`} inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="1700000" />
                    </label>
                    <label>
                      <span className="block text-xs text-muted-foreground mb-1">{t('compensation.effectiveFrom')}</span>
                      <input className={input} type="date" value={effFrom} onChange={(e) => setEffFrom(e.target.value)} />
                    </label>
                    <button className={primaryBtn} onClick={onSave} disabled={busy || !amount.trim()}>
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {t('compensation.save')}
                    </button>
                    <button className={secondaryBtn} onClick={() => { setRevising(false); setAmount(''); }} disabled={busy}>
                      {t('common.cancel', { defaultValue: 'Cancel' })}
                    </button>
                  </div>
                </>
              ) : (
                <button className={secondaryBtn} onClick={() => setRevising(true)}>
                  <Plus className="w-4 h-4" /> {t('compensation.reviseSalary')}
                </button>
              )}
            </div>

            {history && history.length > 0 && (
              <div className="border-t border-border pt-3">
                <div className="text-sm font-medium text-foreground mb-2 flex items-center gap-1.5"><History className="w-4 h-4" /> {t('compensation.history')}</div>
                <ul className="space-y-1.5">
                  {history.map((c, i) => (
                    <li key={i} className="flex items-center justify-between text-sm">
                      <span className="text-foreground">{isWithheld(c.amount) ? t('compensation.restrictedShort', { defaultValue: 'Restricted' }) : fmtMoney(c.amount, c.currency)}</span>
                      <span className="text-xs text-muted-foreground">{fmtDate(c.effective_from)}{c.effective_to ? ` – ${fmtDate(c.effective_to)}` : ` – ${t('compensation.now')}`}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CompensationCard;
